import type { ReactNode } from 'react'

import { cn } from '@/lib/cn'

export interface StatTileProps {
  label: string
  value: string
  hint?: string
  icon?: ReactNode
  tone?: 'default' | 'warning'
  /** Optional 0–1 share, drawn as a meter under the value. */
  share?: number
  className?: string
}

/**
 * A number is often the right form: no chart needed.
 *
 * The meter is a magnitude bar, so it uses the same series colour and the same
 * 4px rounded data end as the charts.
 */
export function StatTile({
  label,
  value,
  hint,
  icon,
  tone = 'default',
  share,
  className,
}: StatTileProps) {
  return (
    <div
      className={cn(
        'rounded-lg border bg-surface-raised p-4 shadow-xs',
        tone === 'warning' ? 'border-warning/40' : 'border-line',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-2xs font-semibold tracking-[0.06em] text-ink-muted uppercase">
          {label}
        </p>
        {icon ? <span className="shrink-0 text-ink-muted">{icon}</span> : null}
      </div>

      <p className="ident mt-2 text-2xl leading-none font-semibold text-ink">
        {value}
      </p>

      {share !== undefined ? (
        <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-chart-track">
          <div
            className="h-full rounded-full bg-series-1"
            style={{ width: `${Math.min(Math.max(share, 0), 1) * 100}%` }}
          />
        </div>
      ) : null}

      {hint ? <p className="mt-1.5 text-xs text-ink-muted">{hint}</p> : null}
    </div>
  )
}
